import React, { useState, useEffect } from 'react';
import CompanyDataCard, { CompanyDataRow } from 'components/reusable/CompanyDataCard';
import { useTranslation } from 'react-i18next';
import useFormatDate from 'hooks/format/useFormatDate';
import CollapsibleTable from 'components/reusable/CollapsibleTable';
import useIsUiHidden from 'hooks/dom/useIsUiHidden';
import { Table } from 'antd';
import ExpandableButton from '../../ExpandableButton';
import { ReactComponent as LegalProceedingsIcon } from 'assets/img/icons/cardIcons/legalProceedings.svg';
import { ReactComponent as LegalProceedingsOtherIcon } from 'assets/img/icons/cardIcons/legalProceedingsOther.svg';
import { isOlderThanXYear } from '../../../../helpers/common';

const LegalProceedingsCard = ({ legalProceedingsData, children }) => {
  const { t } = useTranslation();
  const formatDate = useFormatDate();

  return (
    <CompanyDataCard
      icon={<LegalProceedingsIcon />}
      title={t('professionalReport.negativeData.legalProceedings.title')}
      className={
        legalProceedingsData?.totalDefendant &&
        legalProceedingsData?.latestDateDefendant &&
        !isOlderThanXYear(legalProceedingsData?.latestDateDefendant, 1)
          ? 'negative-class'
          : ''
      }
    >
      <CompanyDataRow
        type={t('professionalReport.negativeData.legalProceedings.numberOfProceedings')}
        value={legalProceedingsData?.total || '-'}
      />
      <CompanyDataRow
        type={t('professionalReport.negativeData.legalProceedings.lastDate')}
        value={legalProceedingsData?.latestDate ? formatDate(legalProceedingsData?.latestDate) : '-'}
      />
      <div className="space-top-md">{children}</div>
    </CompanyDataCard>
  );
};

export const LegalProceedingsOtherCard = ({ legalProceedingsData, children }) => {
  const { t } = useTranslation();
  const formatDate = useFormatDate();

  return (
    <CompanyDataCard
      icon={<LegalProceedingsOtherIcon />}
      title={t('professionalReport.negativeData.legalProceedings.otherInformation.title')}
      className={legalProceedingsData?.total ? 'negative-class' : ''}
    >
      <CompanyDataRow
        type={t('professionalReport.negativeData.legalProceedings.otherInformation.numberOfEntries')}
        value={legalProceedingsData?.total || '-'}
      />
      <CompanyDataRow
        type={t('professionalReport.negativeData.legalProceedings.otherInformation.dateOfLastEntry')}
        value={legalProceedingsData?.latestDate ? formatDate(legalProceedingsData?.latestDate) : '-'}
      />
      {children}
    </CompanyDataCard>
  );
};

/**
 * @param tableData
 * @param type "asDefendant" | "asPlaintiff" | "asThirdParty"
 */
export const LegalProceedingsTable = ({ tableData, type = '', briefPdf, report = null }) => {
  const isUiHidden = useIsUiHidden();
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const handleExpand = (record, e) => {
    e.stopPropagation();
    setExpandedRowKeys(prev =>
      prev.includes(record.key) ? prev.filter(key => key !== record.key) : [...prev, record.key]
    );
  };

  const detailedInfoColumns = [
    {
      title: t('professionalReport.negativeData.legalProceedings.otherInformation.detailedInformation.dateAndCase'),
      dataIndex: ['caseNr', 'processNumber', 'caseType', 'date'],
      width: '20%',
      key: 'processNumber',
      render: (claimsData, { date, processNumber, caseType, caseNr }) => {
        return (
          <>
            <strong>
              <div>{date ? formatDate(date) : '-'}</div>
            </strong>
            <div>{processNumber ? processNumber : '-'}</div>
            <div>
              {caseType} {caseNr}
            </div>
          </>
        );
      },
    },
    {
      title: t('professionalReport.negativeData.legalProceedings.detailedInformation.parties'),
      dataIndex: 'partiesCourt',
      width: '25%',
      render: partiesCourt => <p dangerouslySetInnerHTML={{ __html: partiesCourt }}></p>,
      key: 'partiesCourt',
    },
    {
      title: t('professionalReport.negativeData.legalProceedings.detailedInformation.nature'),
      dataIndex: 'nature',
      width: '25%',
      key: 'nature',
      render: (record, { nature, decision }) => {
        return (
          <>
            <div>
              <strong>{t('professionalReport.negativeData.legalProceedings.detailedInformation.nature')}: </strong>
              {nature ? nature : '-'}
            </div>
            <div>
              <strong>{t('professionalReport.negativeData.legalProceedings.detailedInformation.decision')}: </strong>
              {decision ? decision : '-'}
            </div>
          </>
        );
      },
    },
    {
      title: t('professionalReport.negativeData.legalProceedings.detailedInformation.court'),
      dataIndex: 'court',
      width: '20%',
      key: 'court',
      sorter: (a, b) => a.court.localeCompare(b.court),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: t('professionalReport.negativeData.legalProceedings.otherInformation.detailedInformation.hearings'),
      width: '10%',
      key: 'expand',
      render: (_, record, index) => {
        return record?.additionalRecords ? (
          record.additionalRecords?.length === 0 ? (
            record?.additionalRecords?.length + 1
          ) : (
            <>
              {record?.additionalRecords?.length + 1}{' '}
              {!isUiHidden && (
                <ExpandableButton
                  withText
                  isActive={expandedRowKeys.includes(record.key)}
                  onClick={e => handleExpand(record, e)}
                />
              )}
            </>
          )
        ) : null;
      },
    },
  ];

  const flattenTableData = tableData.map(group => {
    const [first, ...rest] = group.results;
    return {
      ...first,
      key: `${first.caseNr}-${first.date}`,
      additionalRecords: rest,
    };
  });

  useEffect(() => {
    if (isUiHidden) {
      const keysToExpand = flattenTableData.filter(row => row.additionalRecords?.length > 1).map(row => row.key);
      setExpandedRowKeys(keysToExpand);
    }
  }, [isUiHidden]);

  return (
    <CollapsibleTable
      rowKey={record => record.key}
      title={t(`professionalReport.negativeData.legalProceedings.detailedInformation.${type}.title`)}
      disclaimer={
        briefPdf
          ? t('professionalReport.negativeData.legalProceedings.detailedInformation.briefDisclaimer')
          : report === 'start' && tableData.length > 50
          ? t('professionalReport.negativeData.legalProceedings.detailedInformation.last50')
          : null
      }
      data={
        briefPdf ? flattenTableData.slice(0, 10) : report === 'start' ? flattenTableData.slice(0, 50) : flattenTableData
      }
      columns={detailedInfoColumns}
      expandable={{
        expandedRowRender: record =>
          record.additionalRecords?.length ? (
            <Table
              columns={detailedInfoColumns.map(col => ({
                ...col,
                sorter: undefined, // Remove sorter for sub-table
              }))}
              dataSource={record.additionalRecords.map((r, index) => ({
                ...r,
                key: `${r.caseNr}-${r.date}-${index}`,
              }))}
              pagination={false}
              bordered
              className="ant-table--nested"
              rowKey={record => record.rowKey}
            />
          ) : null,
        expandIcon: ({ expanded, onExpand, record }) =>
          record.additionalRecords?.length > 1 ? (
            <ExpandableButton onClick={e => onExpand(record, e)} isActive={expanded} />
          ) : null,
        defaultExpandAllRows: isUiHidden,
        rowExpandable: record => record.additionalRecords?.length > 0,
        expandIconColumnIndex: -1,
        expandedRowKeys,
        onExpandedRowsChange: setExpandedRowKeys,
      }}
      pagination={isUiHidden ? false : { defaultPageSize: 10, showSizeChanger: true }}
      bordered
    />
  );
};

export default LegalProceedingsCard;
